import type { RouteConfig } from "@asteasolutions/zod-to-openapi";
import { defaultErrorResponses, problemResponse, registry } from "../registry.js";
import { z } from "../zod.js";
import {
  Remnant,
  RemnantListResponse,
  RemnantPriceClass,
  RemnantPriceClassCreateRequest,
  RemnantPriceClassListResponse,
  RemnantPriceClassUpdateRequest,
  RemnantReclassRequest,
  RemnantRegisterRequest,
  RemnantRegisterResponse,
  RemnantReserveRequest,
  RemnantScrapRequest,
  WasteCategory,
  WasteCategoryCreateRequest,
  WasteCategoryListResponse,
  WasteCategoryUpdateRequest,
  WasteCauseCode,
  WasteCauseCreateRequest,
  WasteCauseUpdateRequest,
  WasteEvent,
  WasteEventCreateRequest,
  WasteEventCreateResponse,
  WasteEventDispositionRequest,
  WasteEventListResponse,
  WasteEventReverseRequest,
  WasteEventReverseResponse,
  WasteRollupGroupBy,
  WasteRollupResponse,
  WasteSettingsResponse,
  WasteSettingsUpdateRequest,
} from "./schemas.js";

// Module 05 — waste HTTP surface, SLICE 05-A (spec 05 §5): the three catalogs
// (categories + causes + remnant price classes) and the tenant settings
// singleton. The ledger, variance, remnant, PVC, regrind, sale and posting rows
// of §5 register with the slices that implement them, keeping every
// openapi/v1.json change additive-only (oasdiff gate).
//
// PERMISSIONS, straight off §7 (see 0018_waste_catalogs.sql for the cell-by-cell
// transcription):
//   * catalog READS are authenticated-only ("all authenticated", §5 rows 1 and
//     61) — the floor capture grid, the remnant pickers and module 04's scrap
//     reason list all need them, and none of those callers hold `waste.read`.
//   * catalog WRITES carry `waste.catalog.manage` (group_admin).
//   * settings READ carries `waste.read` (narrowed service-side to
//     group_admin / facility_admin / planner, the §5 row 57 role list);
//     settings WRITE carries `waste.settings.manage` (group_admin), audit-
//     chained with the full before- and after-image.

const sessionSecurity = [{ sessionCookie: [] }];
const idempotencyHeader = { $ref: "#/components/parameters/IdempotencyKey" };

function route(config: {
  method: "get" | "post" | "patch" | "put" | "delete";
  path: string;
  operationId: string;
  summary: string;
  permission?: string;
  request?: RouteConfig["request"];
  responses: RouteConfig["responses"];
  description?: string;
}): void {
  const mutation = config.method !== "get";
  registry.registerPath({
    method: config.method,
    path: config.path,
    operationId: config.operationId,
    tags: ["waste"],
    summary: config.summary,
    description:
      (config.description ?? "") +
      (config.permission !== undefined ? ` Requires \`${config.permission}\`.` : ""),
    security: sessionSecurity,
    ...(config.request !== undefined ? { request: config.request } : {}),
    ...(mutation ? { parameters: [idempotencyHeader] } : {}),
    responses: { ...defaultErrorResponses(), ...config.responses },
  });
}

const jsonBody = (schema: z.ZodType) => ({
  body: { content: { "application/json": { schema } } },
});
const ok = (description: string, schema: z.ZodType) => ({
  200: { description, content: { "application/json": { schema } } },
});
const created = (description: string, schema: z.ZodType) => ({
  201: { description, content: { "application/json": { schema } } },
});

const IdParam = z.object({ id: z.guid() });

/**
 * `?is_active=true|false`; absent (or empty) means BOTH.
 *
 * A TWO-VALUE STRING ENUM, not `z.coerce.boolean()`, because the enum is what
 * the server actually implements. `parseBool` in waste-catalog.controller.ts
 * accepts the literals "true" and "false" and answers 422 VALIDATION_FAILED to
 * anything else, deliberately: `?is_active=no` silently coerced to `true` (which
 * is what `z.coerce.boolean()` does to every non-empty string) would answer a
 * different question from the one asked, on a screen that decides which catalog
 * rows a factory sees. Until 2026-08-03 this file declared the coercing schema —
 * a published contract promising semantics the server refuses, kept harmless
 * only by the accident that the generated client serializes booleans as exactly
 * those two words. Declaring the two literals says the same thing to a
 * hand-written client, a curl and the OpenAPI document.
 *
 * The controller does not import this schema (contracts exports DTOs, not query
 * parsers), so `waste-query-contract.test.ts` drives the published document and
 * `parseBool` against each other to keep the two from drifting again.
 */
const IsActiveQuery = z.enum(["true", "false"]).optional();

// ===========================================================================
// categories + causes (spec 05 §2.1, §2.2, §5 rows 1, 2, 3, 59, 60)
// ===========================================================================

route({
  method: "get",
  path: "/v1/waste/categories",
  operationId: "listWasteCategories",
  summary: "The waste category catalog (optionally with cause codes)",
  description:
    "Spec 05 §5 row 1 — readable by ANY authenticated principal: the floor capture grid (§4 S8), the module-04 scrap-reason picker and the QC disposition mapping all need it, and none of those roles hold `waste.read`. Ordered by (sort_order, code) so the grid keeps the vertical pack's order rather than an English alphabetical one. Not paginated: a bounded catalog the floor PWA must hold whole to work offline. `include=causes` adds the cause catalog in the same round trip (the grid needs both to render a 3-tap flow).",
  request: {
    query: z.object({
      is_active: IsActiveQuery,
      include: z.literal("causes").optional(),
    }),
  },
  responses: ok("The category catalog", WasteCategoryListResponse),
});

route({
  method: "post",
  path: "/v1/waste/categories",
  operationId: "createWasteCategory",
  summary: "Add a custom waste category",
  permission: "waste.catalog.manage",
  description:
    "Spec 05 §5 row 3. `source` is always `tenant` on a created row — only a pack install writes `vertical_pack`.",
  request: jsonBody(WasteCategoryCreateRequest),
  responses: {
    ...created("The created category", WasteCategory),
    409: problemResponse("WASTE_CATEGORY_CODE_TAKEN — UNIQUE(tenant, code)"),
  },
});

route({
  method: "put",
  path: "/v1/waste/categories/{id}",
  operationId: "updateWasteCategory",
  summary: "Edit a waste category (name, disposition, flags, order, active)",
  permission: "waste.catalog.manage",
  description:
    "Spec 05 §5 row 2. `code` is not editable — it is the stable key module 04's `scrap_reason_code` FK and the pack's convergence address rows by, so it is absent from the request schema rather than answered with a 409 after the fact. Editing a pack- or system-owned row DEMOTES `source` to `tenant` in the same statement (D-062 guard 1): the pack stops overwriting it on the next install, which is exactly what pressing “Customize” in S11 promises. Emits `waste.category_updated` with the changed column names.",
  request: { params: IdParam, ...jsonBody(WasteCategoryUpdateRequest) },
  responses: {
    ...ok("The updated category", WasteCategory),
    404: problemResponse("WASTE_CATEGORY_NOT_FOUND (cross-tenant ids are invisible)"),
  },
});

route({
  method: "post",
  path: "/v1/waste/causes",
  operationId: "createWasteCause",
  summary: "Add a custom waste cause code",
  permission: "waste.catalog.manage",
  description:
    "Spec 05 §5 row 59. `category_ids` empty (the default) means the cause is offered for every category; a non-empty list restricts the §4 S8 cause grid to those categories and every id is validated against this tenant's categories in the same transaction.",
  request: jsonBody(WasteCauseCreateRequest),
  responses: {
    ...created("The created cause code", WasteCauseCode),
    409: problemResponse("WASTE_CAUSE_CODE_TAKEN — UNIQUE(tenant, code)"),
    422: problemResponse("WASTE_CAUSE_CATEGORY_UNKNOWN — a category_ids element is not a category"),
  },
});

route({
  method: "put",
  path: "/v1/waste/causes/{id}",
  operationId: "updateWasteCause",
  summary: "Edit a waste cause code",
  permission: "waste.catalog.manage",
  description:
    "Spec 05 §5 row 60. `code` is not editable (same reasoning as the category row). Editing a pack-owned row demotes `source` to `tenant`.",
  request: { params: IdParam, ...jsonBody(WasteCauseUpdateRequest) },
  responses: {
    ...ok("The updated cause code", WasteCauseCode),
    404: problemResponse("WASTE_CAUSE_NOT_FOUND"),
    422: problemResponse("WASTE_CAUSE_CATEGORY_UNKNOWN"),
  },
});

// ===========================================================================
// remnant price classes (spec 05 §2.7, §5 rows 61–63)
// ===========================================================================

route({
  method: "get",
  path: "/v1/waste/price-classes",
  operationId: "listRemnantPriceClasses",
  summary: "The off-cut discount ladder",
  description:
    "Spec 05 §5 row 61 — readable by any authenticated principal (a storekeeper re-classing a piece on the floor holds no `waste.read`). Ladder order per §2.7: banded classes first, low band to high — an open-ended LOW band starts at 0, so a NULL `auto_assign_min_length_m` sorts AS 0 and leads the ladder — then fully unbanded, hand-assigned classes last, `code` breaking ties.",
  request: { query: z.object({ is_active: IsActiveQuery }) },
  responses: ok("The price-class ladder", RemnantPriceClassListResponse),
});

route({
  method: "post",
  path: "/v1/waste/price-classes",
  operationId: "createRemnantPriceClass",
  summary: "Add a remnant price class",
  permission: "waste.catalog.manage",
  description:
    "Spec 05 §5 row 62. The auto-assign band is `[min, max)` in metres. Bands of ACTIVE classes may not overlap: two classes both claiming 100 m would make auto-classing answer differently depending on row order, so the overlap is refused rather than resolved arbitrarily.",
  request: jsonBody(RemnantPriceClassCreateRequest),
  responses: {
    ...created("The created price class", RemnantPriceClass),
    409: problemResponse("REMNANT_PRICE_CLASS_CODE_TAKEN"),
    422: problemResponse("PRICE_CLASS_BAND_OVERLAP — the [min, max) band overlaps an active class"),
  },
});

route({
  method: "put",
  path: "/v1/waste/price-classes/{id}",
  operationId: "updateRemnantPriceClass",
  summary: "Edit a remnant price class",
  permission: "waste.catalog.manage",
  description:
    "Spec 05 §5 row 63. `code` is not editable. Editing a pack-owned row demotes `source` to `tenant`.",
  request: { params: IdParam, ...jsonBody(RemnantPriceClassUpdateRequest) },
  responses: {
    ...ok("The updated price class", RemnantPriceClass),
    404: problemResponse("REMNANT_PRICE_CLASS_NOT_FOUND"),
    422: problemResponse("PRICE_CLASS_BAND_OVERLAP"),
  },
});

// ===========================================================================
// settings (spec 05 §2.16, §5 rows 57–58)
// ===========================================================================

route({
  method: "get",
  path: "/v1/waste/settings",
  operationId: "getWasteSettings",
  summary: "Tenant matcher / variance settings",
  permission: "waste.read",
  description:
    "Spec 05 §5 row 57 — group_admin, plus facility_admin and planner read-only (service-enforced on top of `waste.read`, the D-040 two-layer pattern). The row always exists: it is created with the tenant (migration backfill) and again in the pack install transaction, because §3.3's variance engine and §3.5's matcher read it unconditionally and a read that substitutes defaults for a missing row is a read that silently invents configuration.",
  responses: ok("The tenant's settings", WasteSettingsResponse),
});

// ===========================================================================
// SLICE 05-B — the ledger (spec 05 §2.3, §3.1, §5 events rows)
// ===========================================================================

route({
  method: "post",
  path: "/v1/waste/events",
  operationId: "recordWasteEvent",
  summary: "Record a waste event (floor capture / office manual)",
  permission: "waste.event.record",
  description:
    "Spec 05 §3.1 doors 1 and 6 — the HTTP door of the six-door converged `recordEvent`. Idempotent on `client_event_id` (§9.1). A replay returns the ORIGINAL row with `idempotent_replay: true`, only to the user who recorded it, and only for the same capture (D-303). Any other caller, and the recorder acting through an impersonation grant, gets 409 `IDEMPOTENCY_KEY_REUSE` with nothing from the row, and the recorder's own different capture gets the same 409. A version-8 `client_event_id` is the shape the server reserves for the keys it derives itself, and it is refused with the same 409 before anything is looked up, whether or not a row holds it. Mint a v4 or v7 key. The server resolves `shift_instance_id` from the clamped `occurred_at` and the facility shift calendar — EXCEPT for `source='manual'`, which carries NULL shift attribution unless the caller explicitly attributes one, audit-logged (D-102): a backfill keyed days later must never land its cost on whatever shift contained that instant. Costing runs the §3.2 waterfall; a caller-supplied `unit_cost_minor` requires `waste.cost.manual`. When the category `requires_dimensions`, the `remnant` block is REQUIRED and the registry row (+ its module-06 stock unit) is created in the same transaction.",
  request: jsonBody(WasteEventCreateRequest),
  responses: {
    ...created(
      "The recorded event (and its remnant, when the category spawns one)",
      WasteEventCreateResponse,
    ),
    422: problemResponse(
      "WASTE_CAUSE_REQUIRED / WASTE_DIMENSIONS_REQUIRED / WASTE_ITEM_UNKNOWN / UOM_NOT_CONVERTIBLE / WASTE_EVIDENCE_INVALID / WASTE_SHIFT_ATTRIBUTION_FORBIDDEN",
    ),
  },
});

route({
  method: "get",
  path: "/v1/waste/events",
  operationId: "listWasteEvents",
  summary: "The waste ledger (S2)",
  permission: "waste.read",
  description:
    "Spec 05 §5 — cursor-paginated, newest first. Money fields are ABSENT (never null) for roles outside the §7 cost set. `posted=true|false` filters on `finance_posting_id`. `wo_operation_id` narrows to ONE routing operation: the ledger row has carried the column since the first migration and §3.5b's double-count fence is operation-grain, so a caller reconciling a single operation's scrap must be able to ask for exactly it — before 2026-08-20 the parameter was undeclared, and the platform dropped it and answered with every scrap in the facility (D-142).",
  request: {
    query: z.object({
      occurred_from: z.string().optional(),
      occurred_to: z.string().optional(),
      facility_id: z.guid().optional(),
      category_id: z.guid().optional(),
      cause_code_id: z.guid().optional(),
      work_center_id: z.guid().optional(),
      shift_instance_id: z.guid().optional(),
      work_order_id: z.guid().optional(),
      wo_operation_id: z.guid().optional(),
      item_id: z.guid().optional(),
      source: z.string().optional(),
      disposition: z.string().optional(),
      posted: z.enum(["true", "false"]).optional(),
      cursor: z.string().optional(),
      limit: z.coerce.number().int().min(1).max(200).optional(),
    }),
  },
  responses: ok("The ledger page", WasteEventListResponse),
});

route({
  method: "get",
  path: "/v1/waste/events/{id}",
  operationId: "getWasteEvent",
  summary: "Waste event detail",
  description:
    "Spec 05 §5 — readable with `waste.read` OR by the event's own reporter (the floor worker re-opening their capture); enforced service-side, 404 for anything else (never 403 — no oracle).",
  request: { params: IdParam },
  responses: {
    ...ok("The event", WasteEvent),
    404: problemResponse("WASTE_EVENT_NOT_FOUND (cross-tenant and out-of-fence ids are invisible)"),
  },
});

route({
  method: "post",
  path: "/v1/waste/events/{id}/disposition",
  operationId: "setWasteEventDisposition",
  summary: "Set a pending event's disposition",
  permission: "waste.disposition.set",
  description:
    "Spec 05 §3.3's state machine: `pending → reuse|regrind|sell|dispose`, ref requirements per target (`reuse` ⇒ remnant ref, `sell` ⇒ waste_sale or priced remnant, `dispose` ⇒ optional free-text ticket). Transitions out of a non-pending disposition answer 409 DISPOSITION_FINAL — reverse and re-record instead.",
  request: { params: IdParam, ...jsonBody(WasteEventDispositionRequest) },
  responses: {
    ...ok("The updated event", WasteEvent),
    409: problemResponse("DISPOSITION_FINAL — the disposition was already decided"),
  },
});

route({
  method: "post",
  path: "/v1/waste/events/{id}/reverse",
  operationId: "reverseWasteEvent",
  summary: "Reverse a waste event (correction pair)",
  permission: "waste.event.reverse",
  description:
    "Spec 05 §3.1.6 — creates the reversal row; the original is never mutated and the pair nets to ZERO in every aggregate (rollups, recovery, sweeps — trap 7's by-construction rule). 409 when the event is already reversed.",
  request: { params: IdParam, ...jsonBody(WasteEventReverseRequest) },
  responses: {
    ...created("The original + reversal pair", WasteEventReverseResponse),
    409: problemResponse("WASTE_EVENT_ALREADY_REVERSED"),
  },
});

route({
  method: "get",
  path: "/v1/waste/rollups",
  operationId: "getWasteRollups",
  summary: "Dashboard aggregates (S4 + S13 now; S1/S3 in 05-D)",
  permission: "waste.read",
  description:
    "Spec 05 §5 — buckets from `waste_daily_rollups` (the §2.15 projection; always derivable from `waste_events` — the rebuild law is property-tested). `group_by=work_center` replaces the original `machine` (D-101). `group_by=cause` regroups the LEDGER instead, because the projection's bucket key carries no cause column; it applies the rebuild's own live-row filter and facility-timezone business-date expression, so its totals reconcile with the projection's exactly (§2.15). `from`/`to` are FACILITY-local business dates on every grouping, never UTC instants (D-089). Buckets are per-UoM and per-currency: `total_qty` never sums across UoMs, money never across currencies (§9.14). Manual-source events carry NULL shift and appear only in date/facility buckets (D-102); events with no cause appear in a `cause_code_id: null` bucket rather than being dropped.",
  request: {
    query: z.object({
      group_by: WasteRollupGroupBy,
      from: z.string(),
      to: z.string(),
      facility_id: z.guid().optional(),
    }),
  },
  responses: ok("Aggregate buckets", WasteRollupResponse),
});

// ===========================================================================
// SLICE 05-B — remnant registry (spec 05 §2.6, §3.4, §5 remnant rows)
// ===========================================================================

route({
  method: "get",
  path: "/v1/waste/remnants",
  operationId: "listRemnants",
  summary: "Remnant registry list/search (S5)",
  permission: "waste.remnant.manage",
  description:
    "Spec 05 §5 — cursor-paginated, `length_m DESC` default. The §5 role list (storekeeper, sales, planner, facility_admin, group_admin, floor_worker read) maps onto `waste.remnant.manage`; the search-first match PREVIEW bar is 05-C's (stated, not shelled).",
  request: {
    query: z.object({
      item_id: z.guid().optional(),
      kind: z.string().optional(),
      status: z.string().optional(),
      facility_id: z.guid().optional(),
      min_length_m: z.string().optional(),
      max_length_m: z.string().optional(),
      price_class_id: z.guid().optional(),
      location_id: z.guid().optional(),
      q: z.string().optional(),
      cursor: z.string().optional(),
      limit: z.coerce.number().int().min(1).max(200).optional(),
    }),
  },
  responses: ok("The registry page", RemnantListResponse),
});

route({
  method: "post",
  path: "/v1/waste/remnants",
  operationId: "registerRemnant",
  summary: "Register a remnant (floor intake, S9)",
  permission: "waste.remnant.manage",
  description:
    "Spec 05 §3.4a — floor_worker/storekeeper (service-narrowed). When `stock_unit_id` is absent the module-06 stock unit is created through module-06-owned registration IN the same transaction (D-065 shape) — never a waste-side write into inventory tables. Idempotent on `client_event_id`. A replay returns the original remnant with `idempotent_replay: true`, only to the user who registered it, and only for the same piece (D-303). Any other caller, and that user acting through an impersonation grant, gets 409 `IDEMPOTENCY_KEY_REUSE` with nothing from the row. A `stock_unit_id` whose registry row another registration made is the same 409, whoever presents it. Lengths below `waste_settings.min_remnant_track_length_m` are refused: below that a piece is waste, not inventory (§2.16).",
  request: jsonBody(RemnantRegisterRequest),
  responses: {
    ...created("The registered remnant", RemnantRegisterResponse),
    422: problemResponse("REMNANT_BELOW_TRACK_LENGTH / WASTE_ITEM_UNKNOWN"),
  },
});

route({
  method: "get",
  path: "/v1/waste/remnants/{id}",
  operationId: "getRemnant",
  summary: "Remnant detail",
  permission: "waste.remnant.manage",
  request: { params: IdParam },
  responses: {
    ...ok("The remnant", Remnant),
    404: problemResponse("WASTE_REMNANT_NOT_FOUND"),
  },
});

route({
  method: "post",
  path: "/v1/waste/remnants/{id}/reserve",
  operationId: "reserveRemnant",
  summary: "Manually reserve a remnant",
  permission: "waste.remnant.manage",
  description: "Spec 05 §3.4 lifecycle — sales/planner/storekeeper (service-narrowed).",
  request: { params: IdParam, ...jsonBody(RemnantReserveRequest) },
  responses: {
    ...ok("The reserved remnant", Remnant),
    409: problemResponse("REMNANT_NOT_AVAILABLE"),
  },
});

route({
  method: "post",
  path: "/v1/waste/remnants/{id}/release",
  operationId: "releaseRemnant",
  summary: "Release a reservation",
  permission: "waste.remnant.manage",
  request: { params: IdParam },
  responses: {
    ...ok("The released remnant", Remnant),
    409: problemResponse("REMNANT_NOT_RESERVED"),
  },
});

route({
  method: "post",
  path: "/v1/waste/remnants/{id}/reclass",
  operationId: "reclassRemnant",
  summary: "Change a remnant's price class",
  permission: "waste.remnant.manage",
  description: "Spec 05 §3.4 — manual override of auto-classing; audit-logged.",
  request: { params: IdParam, ...jsonBody(RemnantReclassRequest) },
  responses: {
    ...ok("The re-classed remnant", Remnant),
    404: problemResponse("WASTE_REMNANT_NOT_FOUND / REMNANT_PRICE_CLASS_NOT_FOUND"),
  },
});

route({
  method: "post",
  path: "/v1/waste/remnants/{id}/scrap",
  operationId: "scrapRemnant",
  summary: "Scrap a remnant (spawns a dispose waste event)",
  permission: "waste.remnant.manage",
  description:
    "Spec 05 §3.4 — storekeeper/facility_admin (service-narrowed). Terminal; the residual value lands in the ledger as a `dispose`-disposition event in the same transaction.",
  request: { params: IdParam, ...jsonBody(RemnantScrapRequest) },
  responses: {
    ...ok("The scrapped remnant", Remnant),
    409: problemResponse("REMNANT_NOT_AVAILABLE — already reserved/closed"),
  },
});

route({
  method: "put",
  path: "/v1/waste/settings",
  operationId: "updateWasteSettings",
  summary: "Update the tenant matcher / variance settings",
  permission: "waste.settings.manage",
  description:
    "Spec 05 §5 row 58 / §2.16 — group_admin only. Every change appends an audit row carrying the BEFORE and AFTER value of each field that moved (§2.16: “every change audit-logged (before/after values in the hash-chain row)”). Only the fields present in the body change.",
  request: jsonBody(WasteSettingsUpdateRequest),
  responses: ok("The updated settings", WasteSettingsResponse),
});
